<script setup lang="ts">
  import { computed } from 'vue'
  import { fmt, randomId } from '@/functions'

  const props = defineProps<{
    id?: string,
    modelValue: string,
    type?: string,
    label?: string,
    placeholder?: string,
    required?: boolean,
    readonly?: boolean,
    error?: string | string[],
    maxLength?: number,
    rows?: number,
    disabled?: boolean,
    autocomplete?: string,
    list?: string,
    tabindex?: number,
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void,
    (e: 'keydown', event: KeyboardEvent): void,
    (e: 'click', event: MouseEvent): void,
    (e: 'blur', event: FocusEvent): void,
  }>()

  const value = computed({
    set: (val) => {
      emit('update:modelValue', val)
    },
    get: () => props.modelValue,
  })

  const onInput = (event: Event) => {
    const eventTarget = event.target as HTMLInputElement
    emit('update:modelValue', eventTarget.value)
  }

  const defaultId = randomId()
  const inputId = computed(() => props.id ?? defaultId)

  defineOptions({
    inheritAttrs: false,
  })
</script>

<template>
  <div v-bind="$attrs">
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-danger-600">
        *
      </span>
    </label>
    <div class="relative flex items-center">
      <textarea
        v-if="type === 'textarea'"
        :id="inputId"
        class="form-input min-h-20"
        :disabled="disabled"
        :value="value"
        :placeholder="placeholder"
        :rows="rows ?? 3"
        :required="required"
        :readonly="readonly"
        :maxlength="maxLength"
        :list="list"
        :tabindex="tabindex"
        @click="emit('click', $event)"
        @input="onInput"></textarea>
      <input
        v-else
        :id="inputId"
        class="form-input"
        :disabled="disabled"
        :autocomplete="autocomplete ?? 'off'"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :required="required"
        :readonly="readonly"
        :maxlength="maxLength"
        :tabindex="tabindex"
        :list="list"
        @input="onInput"
        @click="emit('click', $event)"
        @keydown="emit('keydown', $event)"
        @blur="emit('blur', $event)">
      <div
        v-if="maxLength !== undefined"
        class="absolute bottom-0.5 right-1 bg-inherit text-2.5 text-gray-500">
        {{ fmt.number(maxLength - value.length) }}
      </div>
      <slot name="inline"></slot>
    </div>
    <slot></slot>
    <app-error-msg :msg="error" />
  </div>
</template>

<style scoped>
  input[list]::-webkit-calendar-picker-indicator,
  input[list]::-webkit-list-button {
    display: none !important;
  }
</style>

<style>
@reference "@/assets/styles/main.css";

.form-input {
  @apply block w-full rounded px-4 text-sm h-[var(--h-input)];
  @apply focus:input-ring border placeholder:text-gray-300;
  @apply disabled:bg-gray-200 disabled:text-gray-400;
}
textarea.form-input {
  @apply py-2;
}

.form-label {
  @apply text-left text-sm font-medium block mb-2;
}
</style>
